'use client'

import type { ReactNode } from 'react'
import { cx } from '../utils/cx'
import { fmtSize } from '../utils/fmtSize'
import type { FileKind } from '../FileThumb'

export function MixList({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cx('st-mix-list', className)}>{children}</div>
}

const DEFAULT_LABELS: Record<FileKind, string> = {
  image: 'TIF',
  video: 'MOV',
  audio: 'WAV',
  mp3: 'MP3',
  raw: 'RAW',
  doc: 'DOC',
  zip: 'ZIP'
}

interface MixRowProps {
  kind?: FileKind
  thumbLabel?: string
  name?: ReactNode
  sub?: ReactNode | ReactNode[]
  size?: number | string
  selected?: boolean
  commentCount?: number
  onToggleSelect?: () => void
  onOpenComments?: () => void
  onOpen?: () => void
  className?: string
}

export function MixRow({
  kind = 'doc',
  thumbLabel,
  name,
  sub,
  size,
  selected,
  commentCount = 0,
  onToggleSelect,
  onOpenComments,
  onOpen,
  className
}: MixRowProps) {
  const subs = Array.isArray(sub) ? sub : sub ? [sub] : []
  const sizeLabel = typeof size === 'number' ? fmtSize(size) : size
  return (
    <div className={cx('st-mx', selected && 'selected', className)} data-kind={kind} onClick={onOpen}>
      <div className="thumb">{thumbLabel || DEFAULT_LABELS[kind] || 'FILE'}</div>
      <div className="meta">
        <div className="mx-name">{name}</div>
        <div className="mx-sub">
          {subs.map((s, i) => (
            <span key={i}>{s}</span>
          ))}
          {selected && <span className="sel">◆ Picked</span>}
        </div>
      </div>
      {sizeLabel && <div className="mx-size">{sizeLabel}</div>}
      <button
        type="button"
        className={cx('ib', selected && 'selected')}
        aria-label="Pick"
        onClick={(e) => {
          e.stopPropagation()
          onToggleSelect?.()
        }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
          <path d="M6 4 L6 22" />
          <path d="M6 4 L18 4 L16 8 L18 12 L6 12" />
        </svg>
      </button>
      <button
        type="button"
        className={cx('ib', commentCount > 0 && 'has-comments')}
        aria-label="Comments"
        onClick={(e) => {
          e.stopPropagation()
          onOpenComments?.()
        }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
          <path d="M4 5 L20 5 L20 16 L12 16 L8 20 L8 16 L4 16 Z" />
        </svg>
        {commentCount > 0 && <span className="cc">{commentCount}</span>}
      </button>
    </div>
  )
}
